// Fee calculation — single source of truth for both client cart preview and
// server-side PaymentIntent creation. Keep pure (no DB / no Stripe SDK) so
// the same numbers render in the buyer's cart UI as in the actual charge.
//
// Pricing structure (decided 2026-05-05):
//   Buyer service fee, applied to subtotal:
//     subtotal < $8       → $1.20 flat (covers Stripe's $0.30 fixed cost)
//     subtotal $8-49.99   → 15%
//     subtotal $50-99.99  → 13%
//     subtotal $100+      → 11%
//   Seller payment processing fee: 1% (or 0% during Founding Maker window)
//   Stripe processing: 2.9% + $0.30 — Stoop absorbs out of the buyer fee.
//
// All math here uses dollars (numbers) for clarity. Conversion to cents for
// Stripe happens at the call site via Math.round(x * 100).

// Minimum order subtotal for express (QR) checkout. Set at $8 so the order
// never falls under the $1.20 fee floor — at/above $8 the buyer fee is a clean
// 15%, which keeps the all-in price proportionate at a market booth.
export const EXPRESS_MIN_ORDER = 8;

// Minimum subtotal for delivery on the guest storefront — aligned with the
// founder free-delivery threshold so the "add more to unlock free delivery"
// nudge is honest.
export const EXPRESS_DELIVERY_MIN = 15;

// Serviceable radius (miles) for storefront delivery. While the founder driver
// covers every run for free, delivery is complimentary within this radius and
// unavailable (pickup only) beyond it.
export const EXPRESS_DELIVERY_RADIUS_MILES = 15;

export interface FeeBreakdown {
  subtotal: number;        // sum of seller listing prices in the cart
  buyerFee: number;        // visible Stoop service fee charged to buyer
  buyerTotal: number;      // what the buyer's card is charged
  stripeProcessing: number; // what Stripe takes (informational; not a line item)
  sellerFee: number;       // 1% of subtotal (or 0 for Founding Maker)
  sellerNet: number;       // what the seller actually receives
  platformNet: number;     // Stoop's profit after Stripe and seller payouts
  // Whether the buyer fee was bumped up to the $1.20 floor (vs the % rate)
  // — UI uses this to render "$1.20 minimum service fee on small orders"
  feeFromFloor: boolean;
  // Which tier was applied — UI uses this to show "you saved by hitting the
  // 13% / 11% tier" or "you'd save $X by adding $Y more"
  tier: "small" | "standard" | "medium" | "large";
}

const SMALL_ORDER_FEE = 1.2;       // flat fee under $8
const TIER_THRESHOLDS = {
  smallMax: 8,                       // < $8 → flat $1.20
  standardMax: 50,                   // $8-49.99 → 15%
  mediumMax: 100,                    // $50-99.99 → 13%
  // $100+ → 11%
};
const RATES = {
  standard: 0.15,
  medium: 0.13,
  large: 0.11,
  seller: 0.01,
  stripePercent: 0.029,
  stripeFixed: 0.3,
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface FeeCalcInput {
  subtotal: number;
  isFoundingMaker?: boolean;
  // Force a specific seller fee rate (override). Used for tests, demo flows,
  // or future tier-based seller rates. Defaults to RATES.seller (or 0 for
  // Founding Makers).
  sellerFeeRate?: number;
}

export function calculateFees(input: FeeCalcInput): FeeBreakdown {
  const subtotal = round2(input.subtotal);
  if (!Number.isFinite(subtotal) || subtotal < 0) {
    throw new Error("Invalid subtotal");
  }

  let buyerFee: number;
  let tier: FeeBreakdown["tier"];
  let feeFromFloor = false;

  if (subtotal < TIER_THRESHOLDS.smallMax) {
    buyerFee = SMALL_ORDER_FEE;
    tier = "small";
    feeFromFloor = true;
  } else if (subtotal < TIER_THRESHOLDS.standardMax) {
    buyerFee = round2(subtotal * RATES.standard);
    tier = "standard";
  } else if (subtotal < TIER_THRESHOLDS.mediumMax) {
    buyerFee = round2(subtotal * RATES.medium);
    tier = "medium";
  } else {
    buyerFee = round2(subtotal * RATES.large);
    tier = "large";
  }

  const buyerTotal = round2(subtotal + buyerFee);
  const stripeProcessing = round2(buyerTotal * RATES.stripePercent + RATES.stripeFixed);

  const sellerRate =
    input.sellerFeeRate ?? (input.isFoundingMaker ? 0 : RATES.seller);
  const sellerFee = round2(subtotal * sellerRate);
  const sellerNet = round2(subtotal - sellerFee);

  const platformNet = round2(buyerTotal - stripeProcessing - sellerNet);

  return {
    subtotal,
    buyerFee,
    buyerTotal,
    stripeProcessing,
    sellerFee,
    sellerNet,
    platformNet,
    feeFromFloor,
    tier,
  };
}

// Guest storefront (express) checkout charges ONLY card processing, not the
// tiered platform fee, to keep buyer friction low while we're growing demand.
// Grossed up so the seller still nets the full subtotal after Stripe's fee.
// Used by both the storefront preview and the express PaymentIntent so the
// shown price matches the charge.
export function calculateExpressFees(input: { subtotal: number }): {
  buyerFee: number;
  buyerTotal: number;
} {
  const subtotal = round2(input.subtotal);
  if (!Number.isFinite(subtotal) || subtotal < 0) {
    throw new Error("Invalid subtotal");
  }
  const buyerTotal = round2((subtotal + RATES.stripeFixed) / (1 - RATES.stripePercent));
  const buyerFee = round2(buyerTotal - subtotal);
  return { buyerFee, buyerTotal };
}

// Maker-invite reward. When a referred grower/maker publishes their first
// listing, whoever invited them earns a discount on the buyer service fee.
// The discount is floored so the fee still covers Stripe (2.9% + $0.30) — the
// transaction breaks even rather than costing the platform — and it's gated to
// orders at/above MAKER_INVITE_MIN_SUBTOTAL, because the fixed $0.30 leaves
// nothing to give away on tiny carts. Same math on the client preview and the
// server charge so the price-mismatch guard never trips.
export const MAKER_INVITE_FEE_REWARD_TYPE = "maker_invite_fee";
export const MAKER_INVITE_FEE_PERCENT = 60;
export const MAKER_INVITE_MIN_SUBTOTAL = 15;

// Lowest the buyer fee can go and still cover Stripe on the discounted total.
// Solves fee = stripePercent * (subtotal + fee) + stripeFixed for fee.
export function breakEvenBuyerFee(subtotal: number): number {
  return round2(
    (RATES.stripePercent * round2(subtotal) + RATES.stripeFixed) / (1 - RATES.stripePercent),
  );
}

// Dollars taken off the buyer service fee for a maker-invite reward at a given
// subtotal. Zero below the min subtotal; never cuts the fee below break-even.
export function makerInviteFeeDiscount(
  subtotal: number,
  percent: number = MAKER_INVITE_FEE_PERCENT,
): number {
  const s = round2(subtotal);
  if (!Number.isFinite(s) || s < MAKER_INVITE_MIN_SUBTOTAL) return 0;
  const { buyerFee } = calculateFees({ subtotal: s });
  const discountedFee = Math.max(breakEvenBuyerFee(s), round2(buyerFee * (1 - percent / 100)));
  return round2(Math.max(0, buyerFee - discountedFee));
}

// Single source of truth for the buyer-facing fee row label, so the cart,
// checkout, and any other pre-purchase surface always show the same text.
export function serviceFeeLabel(
  fees: Pick<FeeBreakdown, "tier" | "feeFromFloor">,
): string {
  if (fees.feeFromFloor) return "Service fee (orders under $8)";
  if (fees.tier === "standard") return "Service fee (15%)";
  if (fees.tier === "medium") return "Service fee (13%)";
  return "Service fee (11%)";
}

// For multi-seller carts: split a cart into per-seller subtotals, calculate
// fees on the COMBINED subtotal (so volume tiers apply to the whole cart),
// then allocate buyer fee and seller fee proportionally per seller. The
// PaymentIntent uses the combined buyerTotal; transfers fan out per seller.
export interface SellerCartItem {
  sellerId: string;
  isFoundingMaker?: boolean;
  subtotal: number; // sum of this seller's items in the cart (gross, pre-discount)
  // Optional per-seller bulk discount: once `subtotal` reaches `discountThreshold`
  // (dollars), take `discountPercent` off this seller's subtotal. Omit / null for
  // no discount — behavior is then identical to before this field existed.
  discountThreshold?: number | null;
  discountPercent?: number | null;
}

export interface SellerSplit {
  sellerId: string;
  subtotal: number;          // gross, pre-discount
  discountAmount: number;    // bulk discount taken off this seller's subtotal
  effectiveSubtotal: number; // subtotal - discountAmount (what fees run on)
  sellerFee: number;
  sellerNet: number;         // what this seller receives via Stripe transfer
}

export interface MultiSellerFees extends FeeBreakdown {
  grossSubtotal: number;  // sum of seller subtotals before any bulk discount
  discountTotal: number;  // total bulk discount across sellers
  perSeller: SellerSplit[];
}

// Bulk discount taken off a single seller's gross subtotal. Returns 0 unless a
// positive percent is set AND the gross meets the threshold.
export function sellerDiscountAmount(
  item: Pick<SellerCartItem, "subtotal" | "discountThreshold" | "discountPercent">,
): number {
  const gross = round2(item.subtotal);
  const threshold = item.discountThreshold ?? null;
  const percent = item.discountPercent ?? null;
  if (threshold == null || percent == null || percent <= 0 || gross < threshold) {
    return 0;
  }
  return round2(gross * (percent / 100));
}

// Total bulk discount across a whole cart, grouped by seller. Both the client
// cart preview and the server charge call this so their subtotals match exactly
// (the server rejects a mismatched client total). Per seller, the most generous
// configured percent wins, paired with its own threshold.
export interface CartDiscountLine {
  sellerId: string;
  lineSubtotal: number;
  discountThreshold?: number | null;
  discountPercent?: number | null;
}

export function cartBulkDiscountTotal(lines: CartDiscountLine[]): number {
  const bySeller = new Map<
    string,
    { gross: number; threshold: number | null; percent: number }
  >();
  for (const l of lines) {
    const cur = bySeller.get(l.sellerId) ?? { gross: 0, threshold: null, percent: 0 };
    cur.gross = round2(cur.gross + l.lineSubtotal);
    const p = l.discountPercent ?? 0;
    if (p > cur.percent) {
      cur.percent = p;
      cur.threshold = l.discountThreshold ?? null;
    }
    bySeller.set(l.sellerId, cur);
  }
  let total = 0;
  for (const s of bySeller.values()) {
    total = round2(
      total +
        sellerDiscountAmount({
          subtotal: s.gross,
          discountThreshold: s.threshold,
          discountPercent: s.percent,
        }),
    );
  }
  return round2(total);
}

export function calculateMultiSellerFees(
  items: SellerCartItem[],
): MultiSellerFees {
  const calc = items.map((i) => {
    const gross = round2(i.subtotal);
    const discountAmount = sellerDiscountAmount(i);
    const effective = round2(gross - discountAmount);
    return { ...i, gross, discountAmount, effective };
  });

  const grossSubtotal = round2(calc.reduce((sum, i) => sum + i.gross, 0));
  const subtotal = round2(calc.reduce((sum, i) => sum + i.effective, 0));
  // Buyer fee + Stripe processing run on the post-discount subtotal.
  const combined = calculateFees({ subtotal });

  // Seller fee is per-seller because Founding Maker status is per-seller.
  const perSeller: SellerSplit[] = calc.map((i) => {
    const sellerRate = i.isFoundingMaker ? 0 : RATES.seller;
    const sellerFee = round2(i.effective * sellerRate);
    const sellerNet = round2(i.effective - sellerFee);
    return {
      sellerId: i.sellerId,
      subtotal: i.gross,
      discountAmount: i.discountAmount,
      effectiveSubtotal: i.effective,
      sellerFee,
      sellerNet,
    };
  });

  // Recompute combined seller totals from the per-seller splits so rounding
  // stays consistent.
  const totalSellerFee = round2(perSeller.reduce((s, p) => s + p.sellerFee, 0));
  const totalSellerNet = round2(perSeller.reduce((s, p) => s + p.sellerNet, 0));
  const discountTotal = round2(perSeller.reduce((s, p) => s + p.discountAmount, 0));
  const platformNet = round2(combined.buyerTotal - combined.stripeProcessing - totalSellerNet);

  return {
    ...combined,
    grossSubtotal,
    discountTotal,
    sellerFee: totalSellerFee,
    sellerNet: totalSellerNet,
    platformNet,
    perSeller,
  };
}

// Helper for UI: "spend $X more to drop your service fee from 15% to 13%"
// We expose rate change rather than absolute "savings" because the absolute
// fee always goes UP when you add items — what changes is the % rate. The
// user-facing pitch is the rate drop, not a literal savings claim.
export function nextTierBenefit(subtotal: number): {
  nextThreshold: number;
  amountToAdd: number;
  currentRatePercent: number;
  nextRatePercent: number;
} | null {
  if (subtotal < TIER_THRESHOLDS.smallMax) {
    // Below $8 the user is on a flat $1.20. Once they hit $8 they're on 15%
    // rate — 15% of $8 is also $1.20, so the visible fee stays the same
    // until they grow further. No tier message worth showing yet.
    return null;
  }
  if (subtotal < TIER_THRESHOLDS.standardMax) {
    return {
      nextThreshold: TIER_THRESHOLDS.standardMax,
      amountToAdd: round2(TIER_THRESHOLDS.standardMax - subtotal),
      currentRatePercent: 15,
      nextRatePercent: 13,
    };
  }
  if (subtotal < TIER_THRESHOLDS.mediumMax) {
    return {
      nextThreshold: TIER_THRESHOLDS.mediumMax,
      amountToAdd: round2(TIER_THRESHOLDS.mediumMax - subtotal),
      currentRatePercent: 13,
      nextRatePercent: 11,
    };
  }
  return null; // already at largest tier
}
